/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import type { FC } from 'react';
import React, { useState, useContext, useCallback, useEffect } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';
import { useQuery } from '@apollo/react-hooks';

import Tabs, { TabList, Tab, useTabPanel } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Box, xcss } from '@atlaskit/primitives';

import {
	IN_EDITOR_TEMPLATES_LOAD_PANEL_EXPERIENCE,
	ExperienceFailure,
} from '@confluence/experience-tracker';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext';
import { TemplatePanel } from '@confluence/template-panel';
import { SourcesList } from '@confluence/import-from-sources';
import { Attribution, ErrorBoundary, ErrorDisplay } from '@confluence/error-boundary';
import { CloseButton, EditorContextPanelItem } from '@confluence/editor-context-panel';
import { layersDialog, layersNavigation } from '@confluence/typography-placeholder';

import type {
	ContentCreationPanelTabsTemplatePanelContentQuery as ContentCreationPanelTabsTemplatePanelContentQuery$data,
	ContentCreationPanelTabsTemplatePanelContentQueryVariables as ContentCreationPanelTabsTemplatePanelContentQuery$variables,
} from './__types__/ContentCreationPanelTabsTemplatePanelContentQuery';

export const i18n = defineMessages({
	templatesTabLabel: {
		id: 'editor-context-panel-content-creation-panel.template-panel.tabs.templates.label',
		defaultMessage: 'Templates',
		description: "Label for 'Templates' tab",
	},
	importDocTabLabel: {
		id: 'editor-context-panel-content-creation-panel.template-panel.tabs.import-doc.label',
		defaultMessage: 'Import',
		description: "Label for 'Import' tab",
	},
	closeLabel: {
		id: 'editor-context-panel-content-creation-panel.close-label',
		defaultMessage: 'Close templates and import',
		description: 'Accessibility label for a cross icon that closes the in-editor context panel',
	},
});
const CONTEXT_PANEL_TAB_TARGET = 'contextPanelTabTarget';
const TEMPLATES_TAB_INDEX = 0;
const IMPORT_TAB_INDEX = 1;

type ContentWrapperProps = {
	disabled?: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div<ContentWrapperProps>({
	backgroundColor: token('elevation.surface'),
	position: 'relative',
	minHeight: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: (props) => (props.disabled ? 'progress' : 'default'),
});

// header > body allows the sticky tabs to be on top of the tab body content as it scrolls
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StickyTabsHeaderWrapper = styled.div({
	position: 'sticky',
	top: token('space.negative.200'),
	paddingBlock: token('space.200'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: layersDialog,
	background: `linear-gradient(180deg, ${token('elevation.surface')} 85%, transparent 100%)`,
});

const closeIconWrapperStyles = xcss({
	height: '0px',
	position: 'absolute',
	right: '0',
	zIndex: 'dialog',
});

const CustomTabPanel: FC<{ children?: React.ReactNode }> = ({ children }) => {
	const tabPanelAttributes = useTabPanel();
	return (
		<div
			{...tabPanelAttributes}
			style={{
				padding: `0 ${token('space.050')}`,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				zIndex: layersNavigation,
			}}
			tabIndex={-1}
		>
			{children}
		</div>
	);
};

const getParentPageId = (
	data: ContentCreationPanelTabsTemplatePanelContentQuery$data | undefined,
): string => {
	const contentAncestors = data?.content?.nodes?.[0]?.ancestors ?? [];
	return contentAncestors.slice(-1)[0]?.id || '';
};

const getInitialTabTarget = (queryParams: object, isImportDisabled?: boolean): number => {
	const tabTarget = queryParams[CONTEXT_PANEL_TAB_TARGET];
	if (tabTarget === 'import' && !isImportDisabled) {
		return IMPORT_TAB_INDEX;
	}

	return TEMPLATES_TAB_INDEX;
};

type ContentCreationPanelTabsProps = {
	currentSpaceKey: string;
	contentId: string;
	title: string;
	onContentUpdated: () => void;
	isPanelDisabled: boolean;
	beforeTemplateSelect?: () => void;
	beforeUpdateRequest?: () => void;
	onError?: () => void;
	closeConfigPanel: () => void;
	hasUserAddedContent: boolean | null;
	abortTemplatePanelLoadExperiences: (reason: string) => void;
	templateAllowList?: string[];
	isImportDisabled?: boolean;
	isAlwaysDiscardChanges?: boolean;
	isSpacePickerDisabled?: boolean;
};

export const ContentCreationPanelTabs: FC<ContentCreationPanelTabsProps> = ({
	currentSpaceKey,
	contentId,
	title,
	onContentUpdated,
	isPanelDisabled,
	beforeTemplateSelect,
	hasUserAddedContent,
	beforeUpdateRequest,
	onError,
	closeConfigPanel,
	abortTemplatePanelLoadExperiences,
	templateAllowList,
	isImportDisabled,
	isAlwaysDiscardChanges,
	isSpacePickerDisabled,
}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { getQueryParams, setQueryParams } = useContext(RoutesContext);
	const [selectedTab, setSelectedTab] = useState(
		getInitialTabTarget(getQueryParams(), isImportDisabled),
	);

	const { data, error, loading } = useQuery<
		ContentCreationPanelTabsTemplatePanelContentQuery$data,
		ContentCreationPanelTabsTemplatePanelContentQuery$variables
	>(
		gql`
			query ContentCreationPanelTabsTemplatePanelContentQuery($contentId: ID!) {
				content(id: $contentId, status: ["current", "draft"]) {
					nodes {
						id
						ancestors {
							id
							title
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId,
			},
			fetchPolicy: 'network-only',
		},
	);

	const parentPageId = getParentPageId(data);

	const handleTabSelect = useCallback(
		(selectedIndex: any) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'editorRightRail',
					action: 'clicked',
					actionSubject: 'tab',
					actionSubjectId: selectedIndex === IMPORT_TAB_INDEX ? 'importTab' : 'templatesTab',
				},
			}).fire();
			setSelectedTab(selectedIndex);
		},
		[setSelectedTab, createAnalyticsEvent],
	);

	useEffect(() => {
		if (selectedTab !== TEMPLATES_TAB_INDEX) {
			abortTemplatePanelLoadExperiences('templates tab is not selected');
		}
	}, [selectedTab, abortTemplatePanelLoadExperiences]);

	useEffect(() => {
		setQueryParams({ [CONTEXT_PANEL_TAB_TARGET]: null }, true);
	}, [setQueryParams]);

	if (loading) return null;

	// if this tabs array gets more complex, we will need to refactor the TEMPLATES_TAB_INDEX and CONTEXT_PANEL_TAB_TARGET concept into something that isnt hardcoded
	const tabs = [
		{
			label: i18n.templatesTabLabel,
			content: (
				<TemplatePanel
					parentPageId={parentPageId}
					currentSpaceKey={currentSpaceKey}
					contentId={contentId}
					title={title}
					onContentUpdated={onContentUpdated}
					isPanelDisabled={isPanelDisabled}
					beforeTemplateSelect={beforeTemplateSelect}
					hasUserAddedContent={hasUserAddedContent}
					beforeUpdateRequest={beforeUpdateRequest}
					onError={onError}
					templateAllowList={templateAllowList}
					isAlwaysDiscardChanges={isAlwaysDiscardChanges}
					isSpacePickerDisabled={isSpacePickerDisabled}
				/>
			),
		},
		...(!isImportDisabled
			? [
					{
						label: i18n.importDocTabLabel,
						content: (
							<SourcesList
								hasUserAddedContent={hasUserAddedContent}
								contentId={contentId}
								parentPageId={parentPageId}
								currentSpace={currentSpaceKey}
							/>
						),
					},
				]
			: []),
	];

	return (
		<EditorContextPanelItem itemName="template-panel" closeSelf={closeConfigPanel}>
			<ErrorBoundary attribution={Attribution.TAILORED_EXPERIENCES}>
				{error && (
					<ErrorDisplay error={error}>
						<ExperienceFailure name={IN_EDITOR_TEMPLATES_LOAD_PANEL_EXPERIENCE} error={error} />
					</ErrorDisplay>
				)}
				<ContentWrapper disabled={isPanelDisabled} data-testid="content-creation-panel">
					<Tabs
						onChange={handleTabSelect}
						selected={selectedTab}
						id="editorContextPanelContentCreationTabs"
					>
						<StickyTabsHeaderWrapper>
							<Box xcss={closeIconWrapperStyles}>
								<CloseButton
									onClose={closeConfigPanel}
									label={intl.formatMessage(i18n.closeLabel)}
								/>
							</Box>
							<TabList>
								{tabs.map((tab, index) => (
									<Tab key={index}>
										<FormattedMessage {...tab.label} />
									</Tab>
								))}
							</TabList>
						</StickyTabsHeaderWrapper>
						{tabs.map((tab, index) => (
							<CustomTabPanel key={index}>{tab.content}</CustomTabPanel>
						))}
					</Tabs>
				</ContentWrapper>
			</ErrorBoundary>
		</EditorContextPanelItem>
	);
};
